import { useState } from "react";

function formatUSPhone(raw) {
  const digits = raw.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function isValidUSPhone(value) {
  return value.replace(/\D/g, "").length === 10;
}

export default function USPhoneInput({
  name = "phone",
  value = "",
  onChange,
  required = false,
  label = "Phone Number",
  placeholder = "(555) 555-5555",
  inputClassName = "",
  inputStyle = {},
}) {
  const [touched, setTouched] = useState(false);

  const handleChange = (e) => {
    const formatted = formatUSPhone(e.target.value);
    onChange({ target: { name, value: formatted } });
  };

  const isValid = isValidUSPhone(value);
  const showError = touched && value.length > 0 && !isValid;
  const showSuccess = touched && isValid;

  return (
    <div>
      {label && (
        <label
          className="block font-constantia text-black-deep mb-1.5"
          style={{ fontSize: "14px", opacity: 0.8 }}
        >
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}

      {/* Input wrapper */}
      <div className="relative flex items-stretch">

        {/* US prefix badge */}
        <div
          className="flex items-center gap-1.5 px-3 rounded-l-lg border border-r-0 flex-shrink-0 select-none"
          style={{
            backgroundColor: "#F7F9FF",
            borderColor: showError
              ? "#FCA5A5"
              : showSuccess
              ? "#86EFAC"
              : "rgba(1,46,114,0.18)",
            borderRight: "none",
          }}
        >
          {/* US flag */}
          <span style={{ fontSize: "16px", lineHeight: 1 }}>🇺🇸</span>
          <span
            className="font-cinzel font-bold text-blue-dark"
            style={{ fontSize: "11px", letterSpacing: "0.05em" }}
          >
            +1
          </span>
        </div>

        {/* Phone input */}
        <input
          type="tel"
          name={name}
          required={required}
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onBlur={() => setTouched(true)}
          className={`flex-1 font-constantia text-black-deep bg-white-pure border outline-none transition-all duration-200 placeholder-gray-400 px-4 py-2.5 text-sm rounded-r-lg ${inputClassName}`}
          style={{
            borderColor: showError
              ? "#FCA5A5"
              : showSuccess
              ? "#86EFAC"
              : "rgba(1,46,114,0.18)",
            boxShadow: showError
              ? "0 0 0 3px rgba(239,68,68,0.08)"
              : showSuccess
              ? "0 0 0 3px rgba(34,197,94,0.08)"
              : undefined,
            fontSize: "15px",
            ...inputStyle,
          }}
          inputMode="tel"
          autoComplete="tel"
        />

        {/* Validation icon */}
        {touched && value.length > 0 && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
            {isValid ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            )}
          </div>
        )}
      </div>

      {/* Error hint */}
      {showError && (
        <p className="font-constantia mt-1 text-xs" style={{ color: "#ef4444" }}>
          Please enter a valid 10-digit US phone number.
        </p>
      )}
    </div>
  );
}
